import { scaleLinear } from 'd3';
import { formatTick } from './format';
import { axisValues, seriesView } from './panelData';
import { axisDomain } from './scales';
import type { PanelAxis, PanelPlan } from './seriesTypes';

// The space around a panel's plot. Desktop: 56 px each side, which holds the
// y labels on the left and the end-of-line name chips on the right. Below
// 900 px (the compact chart) the chips are gone and the margins shrink to
// what the labels need, so the plot gets the width.
export const PANEL_MARGIN = { top: 6, right: 56, bottom: 6, left: 56 };

export interface PanelMargins {
    top: number;
    right: number;
    bottom: number;
    left: number;
    /** Between a y label and the plot. */
    tickGap: number;
}

export const DESKTOP_TICK_GAP = 8;
export const DESKTOP_MARGINS: PanelMargins = {
    ...PANEL_MARGIN,
    tickGap: DESKTOP_TICK_GAP,
};

export const COMPACT_TICK_GAP = 6;
/**
 * The compact margin where no label is: the live dot's halo (4 px, scaled 3x)
 * and the largest alert marker (about 9 px) are drawn past the plot's end.
 */
export const COMPACT_END = 12;
/** The y labels are 10 px monospace: 0.6 em a character in the fonts in use (0.617 in the one the e2e machine has), plus a little. */
export const TICK_CHAR_PX = 6.4;

const WIDEST_PROBABILITY_LABEL = '100%';
const TICK_COUNTS = [3, 5] as const;

export function labelWidth(text: string): number {
    return text.length * TICK_CHAR_PX;
}

/** The widest y label on each side, in px. */
export interface TickWidths {
    left: number;
    right: number;
}

const NO_WIDTHS: TickWidths = { left: 0, right: 0 };

function axisLabelWidth(
    plan: PanelPlan,
    axis: PanelAxis,
    domain: readonly [number, number],
    now: number
): number {
    // A share cannot pass 100 %: what the labels need does not depend on the
    // data, and the margin must not move when the range does.
    if (axis.unit === 'probability') {
        return labelWidth(WIDEST_PROBABILITY_LABEL);
    }
    const stacked = plan.series.some(
        s => s.draw === 'area' && axis.seriesIds.includes(s.id)
    );
    const values = axis.seriesIds.flatMap(id => {
        const series = plan.series.find(s => s.id === id);
        return series ? axisValues(seriesView(series, domain, now)) : [];
    });
    const scale = scaleLinear().domain(
        stacked ? [0, 1] : axisDomain(axis.unit, values)
    );
    let widest = 0;
    // The panel draws 3 ticks on a short plot and 5 on a tall one.
    for (const count of TICK_COUNTS) {
        for (const tick of scale.ticks(count)) {
            widest = Math.max(widest, labelWidth(formatTick(axis.unit, tick)));
        }
    }
    return widest;
}

/**
 * The widest y label of any panel, by side, when `domain` is shown: the labels
 * are measured from the same ticks the panel draws.
 */
export function tickLabelWidths(
    plans: readonly PanelPlan[],
    domain: readonly [number, number],
    now: number
): TickWidths {
    const widths = { ...NO_WIDTHS };
    for (const plan of plans) {
        for (const axis of plan.axes) {
            widths[axis.side] = Math.max(
                widths[axis.side],
                axisLabelWidth(plan, axis, domain, now)
            );
        }
    }
    return widths;
}

export function widest(a: TickWidths, b: TickWidths): TickWidths {
    return {
        left: Math.max(a.left, b.left),
        right: Math.max(a.right, b.right),
    };
}

/**
 * One set of margins for every panel of a stack, so the plots, the time axis
 * and the lifetime bar line up: as wide as the widest label needs, and a few
 * pixels where no label is.
 */
export function compactMargins(widths: TickWidths): PanelMargins {
    const side = (width: number): number =>
        width > 0
            ? Math.max(COMPACT_END, Math.ceil(width) + COMPACT_TICK_GAP)
            : COMPACT_END;
    return {
        top: PANEL_MARGIN.top,
        bottom: PANEL_MARGIN.bottom,
        left: side(widths.left),
        right: side(widths.right),
        tickGap: COMPACT_TICK_GAP,
    };
}

/**
 * Whether a time-axis label centred on `x` lies between `min` and `max` (the
 * plot's coordinates), by the width of its characters. Below 900 px the
 * margins no longer hold a label that sits on the plot's end.
 */
export function timeLabelFits(
    x: number,
    text: string,
    min: number,
    max: number
): boolean {
    const half = labelWidth(text) / 2;
    return x - half >= min && x + half <= max;
}

/**
 * The time-axis labels that fit between `min` and `max`. When none does, all
 * of them: a time axis with no label at all says less than one that touches
 * the edge.
 */
export function fittingTimeLabels<T extends { x: number; text: string }>(
    labels: readonly T[],
    min: number,
    max: number
): T[] {
    const fit = labels.filter(l => timeLabelFits(l.x, l.text, min, max));
    return fit.length > 0 ? fit : [...labels];
}

/**
 * How much of the time axis, from the stack's right edge, the lifetime text
 * covers: its characters, its 8 px of padding and 4 px of air.
 */
export function lifetimeReserve(text: string): number {
    return Math.ceil(labelWidth(text)) + 8 + 4;
}
